import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "@/components/ui/use-toast";
import {
  Check,
  Copy,
  Loader2,
  Mail,
  Trash2,
  UserPlus,
  Users,
} from "lucide-react";
import { useEffect, useState } from "react";
import { supabase } from "../../../supabase/supabase";

interface OrganizationClientMember {
  id: string;
  user_id: string;
  organization_id: string;
  role: "admin" | "editor" | "viewer";
  created_at: string;
  updated_at: string;
  users: {
    id: string;
    email: string;
    full_name?: string;
  };
}

interface ClientInvitation {
  id: string;
  email: string;
  organization_id: string;
  role: string;
  code: string;
  created_at: string;
  expires_at: string;
}

export default function OrganizationClientMembers({
  organizationId,
}: {
  organizationId: string;
}) {
  const [members, setMembers] = useState<OrganizationClientMember[]>([]);
  const [invitations, setInvitations] = useState<ClientInvitation[]>([]);
  const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<"admin" | "editor" | "viewer">(
    "editor",
  );
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMembers, setLoadingMembers] = useState(true);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  useEffect(() => {
    if (organizationId) {
      fetchMembers();
      fetchInvitations();

      // Set up realtime subscription for members
      const membersSubscription = supabase
        .channel(`org-client-members-${organizationId}`)
        .on(
          "postgres_changes",
          {
            event: "*",
            schema: "public",
            table: "organization_client_members",
          },
          () => fetchMembers(),
        )
        .subscribe();

      // Set up realtime subscription for invitations
      const invitationsSubscription = supabase
        .channel(`org-client-invitations-${organizationId}`)
        .on(
          "postgres_changes",
          {
            event: "*",
            schema: "public",
            table: "client_invitations",
          },
          () => fetchInvitations(),
        )
        .subscribe();

      return () => {
        membersSubscription.unsubscribe();
        invitationsSubscription.unsubscribe();
      };
    }
  }, [organizationId]);

  const fetchMembers = async () => {
    if (!organizationId) return;

    try {
      setLoadingMembers(true);

      // First get the client members
      const { data: membersData, error: membersError } = await supabase
        .from("organization_client_members")
        .select("*")
        .eq("organization_id", organizationId);

      if (membersError) throw membersError;

      // Then fetch the user details for each member
      if (membersData && membersData.length > 0) {
        const membersWithUsers = await Promise.all(
          membersData.map(async (member) => {
            const { data: userData, error: userError } = await supabase
              .from("users")
              .select("id, email, full_name")
              .eq("id", member.user_id)
              .single();

            if (userError) {
              console.error("Error fetching user data:", userError);
              return {
                ...member,
                users: {
                  id: member.user_id,
                  email: "Unknown",
                  full_name: null,
                },
              };
            }

            return { ...member, users: userData };
          }),
        );

        setMembers(membersWithUsers);
      } else {
        setMembers([]);
      }
    } catch (error) {
      console.error("Error fetching organization client members:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load organization client members",
      });
    } finally {
      setLoadingMembers(false);
    }
  };

  const fetchInvitations = async () => {
    if (!organizationId) return;

    try {
      const { data, error } = await supabase
        .from("client_invitations")
        .select("*");

      if (error) throw error;
      setInvitations(data || []);
    } catch (error) {
      console.error("Error fetching client invitations:", error);
    }
  };

  const inviteClientMember = async () => {
    if (!inviteEmail.trim() || !organizationId) return;

    try {
      setIsLoading(true);

      // Create a client invitation directly in the database
      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + 7); // Expires in 7 days

      const code =
        Math.random().toString(36).substring(2, 15) +
        Math.random().toString(36).substring(2, 15);

      const { data, error } = await supabase
        .from("client_invitations")
        .insert({
          organization_id: organizationId,
          email: inviteEmail,
          role: inviteRole,
          code: code,
          expires_at: expiresAt.toISOString(),
        })
        .select();

      if (error) throw error;

      toast({
        title: "Invitation sent",
        description: `An invitation has been created for ${inviteEmail}`,
      });

      setInviteEmail("");
      setInviteRole("editor");
      setIsInviteDialogOpen(false);
      fetchInvitations();
    } catch (error) {
      console.error("Error inviting client member:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to send invitation",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const cancelInvitation = async (invitationId: string) => {
    try {
      const { error } = await supabase
        .from("client_invitations")
        .delete()
        .eq("id", invitationId);

      if (error) throw error;

      toast({
        title: "Invitation cancelled",
        description: "The invitation has been cancelled",
      });

      fetchInvitations();
    } catch (error) {
      console.error("Error cancelling invitation:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to cancel invitation",
      });
    }
  };

  const updateMemberRole = async (memberId: string, newRole: string) => {
    try {
      const { error } = await supabase
        .from("organization_client_members")
        .update({ role: newRole })
        .eq("id", memberId);

      if (error) throw error;

      toast({
        title: "Role updated",
        description: "Client member role has been updated",
      });

      fetchMembers();
    } catch (error) {
      console.error("Error updating client member role:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to update client member role",
      });
    }
  };

  const removeMember = async (memberId: string) => {
    try {
      const { error } = await supabase
        .from("organization_client_members")
        .delete()
        .eq("id", memberId);

      if (error) throw error;

      toast({
        title: "Member removed",
        description: "The client member has been removed from the organization",
      });

      fetchMembers();
    } catch (error) {
      console.error("Error removing client member:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to remove client member",
      });
    }
  };

  return (
    <>
      <div className="flex justify-between items-center mb-4">
        <div>
          <h3 className="text-lg font-medium text-foreground">
            Client Members
          </h3>
          <p className="text-sm text-muted-foreground">
            Manage client members who can access campaigns and leads
          </p>
        </div>
        <Button
          onClick={() => setIsInviteDialogOpen(true)}
          size="sm"
          className="rounded-none"
        >
          <UserPlus className="h-4 w-4 mr-2" /> Invite Client
        </Button>
      </div>

      {loadingMembers ? (
        <div className="flex justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : (
        <>
          {members.length === 0 && invitations.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No client members in this organization yet.</p>
              <p className="text-sm mt-2">
                Invite clients to access their campaigns and leads.
              </p>
              <Button
                onClick={() => setIsInviteDialogOpen(true)}
                variant="outline"
                className="mt-4 rounded-none"
              >
                Invite Client
              </Button>
            </div>
          ) : (
            <Tabs defaultValue="members" className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-4 rounded-none">
                <TabsTrigger value="members" className="rounded-none">
                  Members
                </TabsTrigger>
                <TabsTrigger value="invitations" className="rounded-none">
                  Invitations
                </TabsTrigger>
              </TabsList>

              <TabsContent value="members" className="space-y-4">
                {members.length > 0 ? (
                  <div className="space-y-3">
                    {members.map((member) => (
                      <div
                        key={member.id}
                        className="flex items-center justify-between border border-border rounded-none p-3 bg-card text-foreground"
                      >
                        <div>
                          <p className="font-medium text-foreground">
                            {member.users.full_name || member.users.email}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {member.users.email} • Joined{" "}
                            {new Date(member.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Select
                            defaultValue={member.role}
                            onValueChange={(value) =>
                              updateMemberRole(member.id, value)
                            }
                          >
                            <SelectTrigger className="w-[110px] rounded-none">
                              <SelectValue placeholder="Role" />
                            </SelectTrigger>
                            <SelectContent className="rounded-none">
                              <SelectItem value="admin">Admin</SelectItem>
                              <SelectItem value="editor">Editor</SelectItem>
                              <SelectItem value="viewer">Viewer</SelectItem>
                            </SelectContent>
                          </Select>

                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => removeMember(member.id)}
                            className="rounded-none"
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <p>No active client members yet.</p>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="invitations" className="space-y-4">
                {invitations.length > 0 ? (
                  <div className="space-y-3">
                    {invitations.map((invitation) => (
                      <div
                        key={invitation.id}
                        className="flex items-center justify-between border border-border rounded-none p-3 bg-muted/30 text-foreground"
                      >
                        <div>
                          <div className="flex items-center">
                            <p className="font-medium text-foreground">
                              {invitation.email}
                            </p>
                            <Badge
                              variant="outline"
                              className="ml-2 rounded-none"
                            >
                              Pending
                            </Badge>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            Invited{" "}
                            {new Date(
                              invitation.created_at,
                            ).toLocaleDateString()}
                            {" • "}
                            Expires{" "}
                            {new Date(
                              invitation.expires_at,
                            ).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className="rounded-none">
                            {invitation.role}
                          </Badge>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              const invitationLink = `${window.location.origin}/client/signup?code=${invitation.code}`;
                              navigator.clipboard.writeText(invitationLink);
                              setCopiedId(invitation.id);
                              setTimeout(() => setCopiedId(null), 2000);
                              toast({
                                title: "Copied",
                                description:
                                  "Invitation link copied to clipboard",
                              });
                            }}
                            title="Copy invitation link"
                            className="rounded-none"
                          >
                            {copiedId === invitation.id ? (
                              <Check className="h-4 w-4 text-primary" />
                            ) : (
                              <Copy className="h-4 w-4 text-muted-foreground" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => cancelInvitation(invitation.id)}
                            title="Cancel invitation"
                            className="rounded-none"
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <p>No pending invitations.</p>
                    <Button
                      onClick={() => setIsInviteDialogOpen(true)}
                      variant="outline"
                      className="mt-4 rounded-none"
                    >
                      Invite Client
                    </Button>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          )}
        </>
      )}

      {/* Invite dialog */}
      <Dialog open={isInviteDialogOpen} onOpenChange={setIsInviteDialogOpen}>
        <DialogContent className="rounded-none">
          <DialogHeader>
            <DialogTitle>Invite Client Member</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                type="email"
                placeholder="client@example.com"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                className="bg-background border-border focus:border-primary focus:ring-0 text-foreground rounded-none"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="role">Role</Label>
              <Select value={inviteRole} onValueChange={setInviteRole as any}>
                <SelectTrigger id="role" className="rounded-none">
                  <SelectValue placeholder="Select a role" />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="editor">Editor</SelectItem>
                  <SelectItem value="viewer">Viewer</SelectItem>
                </SelectContent>
              </Select>
              <div className="text-xs text-muted-foreground mt-2">
                <p>
                  <strong>Admin:</strong> Can manage campaigns, leads, and other
                  client members
                </p>
                <p>
                  <strong>Editor:</strong> Can manage campaigns and leads
                </p>
                <p>
                  <strong>Viewer:</strong> Can only view campaigns and leads
                </p>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsInviteDialogOpen(false)}
              className="rounded-none"
            >
              Cancel
            </Button>
            <Button
              onClick={inviteClientMember}
              disabled={!inviteEmail.trim() || isLoading}
              className="bg-primary text-primary-foreground hover:bg-primary/80 rounded-none"
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Mail className="mr-2 h-4 w-4" />
                  Send Invitation
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
